import * as React from 'react';

import { RaiselyDonateEmbed } from '@/components/layout/raisely-donate';

const donatePage = {
  eyebrow: 'Donate',
  title: 'Donate to NextOnVera',
  lead: 'Every gift, at every level, pools into the same Brain Cancer Fund — backing NextOnVera and early-stage brain cancer research that traditional funding won’t touch yet. Secure a tax-deductible donation via Tour de Cure.'
};

export function DonatePage() {
  React.useEffect(() => {
    const previous = document.title;
    document.title = 'Donate to NextOnVera | NextOnVera';
    return () => {
      document.title = previous;
    };
  }, []);

  return (
    <section className="relative isolate overflow-hidden">
      {/* Site-themed backdrop image at 70% transparency, layered over the warm paper base */}
      <div aria-hidden="true" className="bg-background absolute inset-0 -z-20" />
      <img
        src="/generated-images/donate-page-backdrop.png"
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 h-full w-full object-cover object-center opacity-30"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-b from-background/60 via-transparent to-background/70"
      />

      <article className="px-4 py-14 sm:px-6 sm:py-16 lg:px-8">
        <div className="mx-auto w-full max-w-6xl">
          <p className="text-primary mb-3 text-xs font-medium tracking-[0.2em] uppercase">
            {donatePage.eyebrow}
          </p>
          <h1 className="font-display text-foreground text-4xl font-semibold tracking-tight sm:text-5xl">
            {donatePage.title}
          </h1>
          <p className="text-muted-foreground mt-5 max-w-2xl text-base leading-relaxed sm:text-lg">
            {donatePage.lead}
          </p>

          {/* Donation window (70% width, left) + donation notes panel (right) */}
          <div className="mt-10 grid grid-cols-1 items-start gap-8 lg:grid-cols-10">
            <div className="lg:col-span-7">
              <div className="border-primary/40 relative overflow-hidden rounded-2xl border-2 bg-white shadow-[0_24px_60px_-40px_rgb(10_122_147_/_0.45)] ring-4 ring-[#0A7A93]/10">
              {/* NextOnVera header strip */}
              <div className="border-primary/30 flex items-center justify-between gap-4 border-b bg-gradient-to-r from-[#0A7A93]/10 via-transparent to-[#0A7A93]/10 px-5 py-3.5 sm:px-6">
                <div className="flex items-center gap-2.5">
                  <span className="relative flex h-3 w-3">
                    <span className="bg-primary/30 absolute inline-flex h-full w-full rounded-full" />
                    <span className="bg-primary relative inline-flex h-3 w-3 rounded-full" />
                  </span>
                  <p className="font-display text-primary text-sm font-semibold tracking-[0.18em] uppercase">
                    NextOnVera
                  </p>
                </div>
                <p className="text-muted-foreground hidden text-xs tracking-wide sm:block">
                  Donations power the NextOnVera platform and early-stage research
                </p>
              </div>

              <RaiselyDonateEmbed height={800} />
              </div>
            </div>
            {/* Donation notes — where the money goes */}
            <aside className="border-primary/30 rounded-2xl border bg-white/80 p-6 shadow-[0_16px_40px_-30px_rgb(10_122_147_/_0.4)] sm:p-7 lg:col-span-3">
              <h2 className="font-display text-foreground text-lg font-semibold tracking-tight sm:text-xl">
                Where your donation goes
              </h2>
              <p className="text-muted-foreground mt-3 text-sm leading-relaxed">
                Every gift pools into the Brain Cancer Fund, governed by Tour
                de Cure’s twenty-year grants discipline.
              </p>
              <ul className="mt-5 space-y-4">
                {[
                  {
                    title: 'NextOnVera platform',
                    body: 'Keeps the oncology decision platform moving — turning funded science into a tool clinicians and patients can actually use.'
                  },
                  {
                    title: 'Early-stage research',
                    body: 'Backs high-risk, high-potential brain cancer ideas too early for NHMRC or MRFF, but too important to lose.'
                  },
                  {
                    title: 'Multi-year certainty',
                    body: 'Multi-year gifts give the research grants pool the certainty to plan bold, longer-horizon work.'
                  }
                ].map((item) => (
                  <li key={item.title} className="flex gap-3">
                    <span aria-hidden="true" className="bg-primary mt-1.5 h-2 w-2 shrink-0 rounded-full" />
                    <div>
                      <p className="text-foreground text-sm font-semibold">{item.title}</p>
                      <p className="text-muted-foreground mt-1 text-sm leading-relaxed">{item.body}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <p className="text-muted-foreground mt-6 border-t border-border/60 pt-4 text-xs leading-relaxed">
                An independent, competitive process directs every dollar — with
                full transparency on what is funded and what is found.
              </p>
            </aside>
          </div>
        </div>
      </article>
    </section>
  );
}
